import React, { useCallback, useMemo, useContext, useRef } from 'react';
import { result, filter, isFunction, reduce, map, isEmpty, chain, uniq, size, noop } from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { ValidationGroup, withError, withCustomValidation } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Label } from '@bingads-webui-react/primitive';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { ActionButton, CommandButton } from '@fluentui/react';
import { registerIcons } from '@fluentui/style-utilities';
import { withContainerErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ValidatedTextFieldWithAdCustomizer } from '@bingads-webui-campaign-react/responsive-search-ad-editor';

import { getStyles } from './style';
import { DummyIsOptionalValidationItem, createValidationPropsForDummyItem } from './dummy-is-optional-validation-item';
import { countTextAssets } from './shared';

registerIcons({
  icons: {
    'delete-svg': (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 3H9C9 2.44772 8.55228 2 8 2C7.44772 2 7 2.44772 7 3ZM6 3C6 1.89543 6.89543 1 8 1C9.10457 1 10 1.89543 10 3H14C14.2761 3 14.5 3.22386 14.5 3.5C14.5 3.77614 14.2761 4 14 4H13.4364L12.2313 12.8378C12.0624 14.0765 11.0044 15 9.75422 15H6.24578C4.99561 15 3.93762 14.0765 3.76871 12.8378L2.56355 4H2C1.72386 4 1.5 3.77614 1.5 3.5C1.5 3.22386 1.72386 3 2 3H6ZM7 6.5C7 6.22386 6.77614 6 6.5 6C6.22386 6 6 6.22386 6 6.5V11.5C6 11.7761 6.22386 12 6.5 12C6.77614 12 7 11.7761 7 11.5V6.5ZM9.5 6C9.77614 6 10 6.22386 10 6.5V11.5C10 11.7761 9.77614 12 9.5 12C9.22386 12 9 11.7761 9 11.5V6.5C9 6.22386 9.22386 6 9.5 6ZM4.75954 12.7027C4.86089 13.4459 5.49568 14 6.24578 14H9.75422C10.5043 14 11.1391 13.4459 11.2405 12.7027L12.4272 4H3.57281L4.75954 12.7027Z" fill="#212121" />
      </svg>
    ),
  },
});

const ValidationGroupWithMessage = withControlErrorHandling(withContainerErrors(withError(ValidationGroup)));
const hasEnoughNonEmptyValues = (list, min) => filter(list.items, item => item.value && result(item.value, 'length', 0) > 0).length >= min;
const hasDuplicateValues = (list) => {
  const values = chain(list.items).filter(item => item.value && result(item.value, 'length', 0) > 0).map(item => item.value).value();
  return size(values) !== size(uniq(values));
};

const deriveValue = (data, minCollectionSize, singleAssetValidationPropsField, validationPropsField) => {
  const initialValue = {};

  const collectionObj = result(data, validationPropsField, {});
  const numberOfPreloadedAssets = countTextAssets({ collectionObj, countEmpty: true });

  for (let i = 0; i < (numberOfPreloadedAssets || minCollectionSize || 1); i += 1) {
    const fieldName = `${singleAssetValidationPropsField}${i}`;
    initialValue[fieldName] = collectionObj[fieldName] || '';
  }
  return initialValue;
};

export const UnvalidatedTextAssetCollection = React.memo((props) => {
  const {
    labelAction,
    overrideInputNode,
    minCollectionSize,
    maxCollectionSize,
    isOptional,
    labelText,
    labelId,
    helpPopupTopic,
    helpPopupAriaLabel,
    placeholderText,
    fieldName,
    singleAssetValidationPropsField,
    validationPropsField,
    addButtonText,
    formErrorProps,
    renderWrapperFunction,
    updateValidation,
    errorMessage,
    duplicateErrorMessage,
    validationProps,
    externalErrorMessages,
    showAddButton,
    hideAddButtonWhenLimitReached,
    getCustomErrorMessage,
    styleOverrides,
    alwaysShowError,
    showDeleteButton,
    onDeleteTextAsset,
    deleteButtonLabel,
    renderInlineCopilot,
    enableCustomizer,
    withDeleteIconDisabled,
  } = props;
  const getStylesWithOverrides = useMemo(() => getStyles(styleOverrides), [styleOverrides]);
  const classes = useDefaultStyles(getStylesWithOverrides);
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);

  const { data, schema } = validationProps;

  const value = { [validationPropsField]: deriveValue(data, minCollectionSize, singleAssetValidationPropsField, validationPropsField) };

  const assetsToRender = value[validationPropsField];

  const onDeleteAsset = useCallback((key) => {
    const deletedValue = value[validationPropsField][key];

    const remainingEntries = Object.entries(value[validationPropsField])
      .filter(([k]) => k !== key);

    const newValidationPropsField = remainingEntries.reduce((acc, [, val], index) => {
      acc[`${singleAssetValidationPropsField}${index}`] = val;
      return acc;
    }, {});

    const padEntries = Math.max(0, minCollectionSize - remainingEntries.length);
    const paddedEntries = new Array(padEntries).fill('').reduce((acc, _, index) => {
      acc[`${singleAssetValidationPropsField}${remainingEntries.length + index}`] = '';
      return acc;
    }, newValidationPropsField);

    if (remainingEntries.length === 0) {
      paddedEntries[`${singleAssetValidationPropsField}0`] = '';
    }

    const newValue = {
      ...value,
      [validationPropsField]: paddedEntries,
    };

    onDeleteTextAsset(key, deletedValue);
    updateValidation(undefined, newValue);
  }, [
    minCollectionSize,
    onDeleteTextAsset,
    singleAssetValidationPropsField,
    updateValidation,
    validationPropsField,
    value,
  ]);

  const renderItem = useCallback((key, itemValue, additionalTextFieldProps) => (
    <div className={classnames(classes.TextAssetContainer, (showDeleteButton || renderInlineCopilot) && classes.WithFlexButtons)} >
      {
        enableCustomizer ?
          <ValidatedTextFieldWithAdCustomizer
            className="input-asset-text"
            i18n={i18n}
            validationProps={{
              field: `${validationPropsField}/${key}`,
              schema,
            }}
            configProps={{
              key: validationPropsField,
              placeholder: placeholderText,
              title: itemValue,
              ...additionalTextFieldProps,
            }}
            onChange={additionalTextFieldProps && additionalTextFieldProps.onChange}
          /> :
          <RemainingCharsValidatedTextField
            className="input-asset-text"
            placeholder={placeholderText}
            i18n={i18n}
            title={itemValue}
            validationProps={{
              field: `${validationPropsField}/${key}`,
              schema,
            }}
            {...additionalTextFieldProps}
          />
      }
      {additionalTextFieldProps && additionalTextFieldProps.renderInlineCopilot && additionalTextFieldProps.renderInlineCopilot(key, itemValue)}
      {showDeleteButton &&
        <CommandButton
          className="delete-asset-btn"
          iconProps={{ iconName: 'delete-svg' }}
          onClick={() => { onDeleteAsset(key); }}
          ariaLabel={deleteButtonLabel}
          disabled={Object.keys(assetsToRender).length === 1 && withDeleteIconDisabled}
        />
      }
    </div>
  ), [classes.TextAssetContainer, classes.WithFlexButtons, showDeleteButton, renderInlineCopilot, enableCustomizer, withDeleteIconDisabled, i18n, validationPropsField, schema, placeholderText, deleteButtonLabel, assetsToRender, onDeleteAsset]);

  const textAssetFields = useMemo(() => map(assetsToRender, (itemValue, key) => {
    if (isFunction(renderWrapperFunction)) {
      return renderWrapperFunction(key, additionalTextFieldProps => renderItem(key, itemValue, additionalTextFieldProps), itemValue);
    }

    return renderItem(key);
  }), [assetsToRender, renderWrapperFunction, renderItem]);

  const hideAddButton = hideAddButtonWhenLimitReached && textAssetFields.length >= maxCollectionSize;

  const onAddAsset = useCallback(() => {
    const newValue = {
      [validationPropsField]: {
        ...value[`${validationPropsField}`],
        [`${singleAssetValidationPropsField}${textAssetFields.length}`]: '',
      },
    };

    updateValidation(undefined, newValue);
  }, [updateValidation, singleAssetValidationPropsField, textAssetFields.length, validationPropsField, value]);

  const onValidate = useCallback((validationResult) => {
    if (activity) {
      activity.signal(`onTextAssetsCollectionValidate- ${validationResult.data}`);
    }
    let customErrors = [];
    const newData = {
      ...validationResult.data,
      [validationPropsField]: {
        ...value[validationPropsField],
        ...validationResult.data[validationPropsField],
      },
    };
    if (getCustomErrorMessage) {
      const assets = newData[validationPropsField];

      customErrors = getCustomErrorMessage(assets, validationPropsField);
    }
    updateValidation([...validationResult.messages, ...customErrors], newData);
  }, [activity, getCustomErrorMessage, updateValidation, validationPropsField, value]);

  const isOptionalRef = useRef();
  isOptionalRef.current = isOptional;

  const collectionValidator = (changed, list) => {
    let groupErrors = hasEnoughNonEmptyValues(list, isOptionalRef.current ? 0 : minCollectionSize) ? [] : [errorMessage];
    if (isEmpty(groupErrors) && duplicateErrorMessage) {
      groupErrors = hasDuplicateValues(list) ? [duplicateErrorMessage] : [];
    }
    return reduce(list.items, (errors, item) => [...errors, ...item.messages], groupErrors);
  };

  const dummyItemValidationProps = useMemo(() => createValidationPropsForDummyItem(isOptional), [isOptional]);

  return (
    <div className={classnames('text-assets-collection-container', `text-assets-collection-container-${fieldName}`, classes.TextAssetsCollectionContainer)}>
      <ValidationGroupWithMessage
        validationProps={{
          onValidate,
          validator: collectionValidator,
          errorFilter: error => error === errorMessage || (duplicateErrorMessage && (error === duplicateErrorMessage)),
          stopPropagation: true,
          passiveMessages: externalErrorMessages,
          showErrorOnPassiveMessages: true,
          ...(alwaysShowError && { alwaysShowError }),
        }}
        formErrorProps={formErrorProps}
      >
        <div className={classnames('input-label-container', classes.labelContainer)}>
          <Label id={labelId} label={classnames('input-label', `input-label-${fieldName}`)}>{labelText}
            {helpPopupTopic && <HelpPopup topic={helpPopupTopic} aria-label={helpPopupAriaLabel} />}
          </Label>
          {labelAction}
        </div>
        {overrideInputNode}
        <div
          style={{
            display: overrideInputNode ? 'none' : 'block',
          }}
        >
          {textAssetFields}
        </div>
        <DummyIsOptionalValidationItem validationProps={dummyItemValidationProps} />
      </ValidationGroupWithMessage>
      {showAddButton && !hideAddButton &&
        <ActionButton
          className={classnames('add-asset-btn', classes.AddAssetButton)}
          iconProps={{ iconName: 'Add' }}
          allowDisabledFocus
          disabled={textAssetFields.length >= maxCollectionSize}
          onClick={onAddAsset}
        >
          {addButtonText}
        </ActionButton>
      }
    </div>
  );
});

UnvalidatedTextAssetCollection.propTypes = {
  minCollectionSize: PropTypes.number,
  maxCollectionSize: PropTypes.number.isRequired,
  isOptional: PropTypes.bool,
  labelText: PropTypes.string,
  labelId: PropTypes.string,
  helpPopupTopic: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  placeholderText: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  validationPropsField: PropTypes.string.isRequired,
  singleAssetValidationPropsField: PropTypes.string.isRequired,
  addButtonText: PropTypes.string.isRequired,
  i18n: PropTypes.shape({}).isRequired,
  formErrorProps: PropTypes.shape({}),
  renderWrapperFunction: PropTypes.func,
  updateValidation: PropTypes.func.isRequired,
  errorMessage: PropTypes.string.isRequired,
  duplicateErrorMessage: PropTypes.string,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({}).isRequired,
    schema: PropTypes.shape({}).isRequired,
  }).isRequired,
  alwaysShowError: PropTypes.bool,
  externalErrorMessages: PropTypes.arrayOf(PropTypes.string),
  showAddButton: PropTypes.bool,
  hideAddButtonWhenLimitReached: PropTypes.bool,
  getCustomErrorMessage: PropTypes.func,
  styleOverrides: PropTypes.shape({}),
  showDeleteButton: PropTypes.bool,
  onDeleteTextAsset: PropTypes.func,
  deleteButtonLabel: PropTypes.string,
  renderInlineCopilot: PropTypes.func,
  enableCustomizer: PropTypes.bool,
  withDeleteIconDisabled: PropTypes.bool,
  labelAction: PropTypes.node,
  overrideInputNode: PropTypes.node,
};

UnvalidatedTextAssetCollection.defaultProps = {
  minCollectionSize: 1,
  isOptional: false,
  withDeleteIconDisabled: false,
  labelText: undefined,
  labelId: undefined,
  placeholderText: undefined,
  formErrorProps: undefined,
  renderWrapperFunction: undefined,
  helpPopupTopic: undefined,
  helpPopupAriaLabel: undefined,
  alwaysShowError: undefined,
  externalErrorMessages: [],
  showAddButton: true,
  hideAddButtonWhenLimitReached: false,
  getCustomErrorMessage: undefined,
  duplicateErrorMessage: undefined,
  styleOverrides: {},
  showDeleteButton: false,
  onDeleteTextAsset: noop,
  deleteButtonLabel: undefined,
  renderInlineCopilot: null,
  enableCustomizer: false,
  labelAction: null,
  overrideInputNode: null,
};

export const TextAssetCollection = withCustomValidation(UnvalidatedTextAssetCollection);

